import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import CustomTable from '@common/CustomTable/Table'
import { TextCell } from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { ChangeHistoryRow } from '@/api/changeHistory'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import { getMessageText } from '@/helpers/messageText'
import cn from './index.module.css'

const entities: Record<string, string> = {
  timetable: 'Серия занятий',
  timetable_report: 'Занятие',
  timetable_properties: 'Параметры расписания',
  timetable_notes: 'Заметка',
  timetable_enrollments: 'Запись на занятие',
  timetable_audience: 'Аудитория',
  journal_timetable: 'Посещаемость',
  journal_grades: 'Оценка',
  journal_columns: 'Колонка журнала',
  journal_column_values: 'Значение колонки',
  journal_additional_columns: 'Дополнительная колонка',
  journal_additional_column_values: 'Значение дополнительной колонки',
  journal_periods: 'Период',
  ktp_journal: 'КТП',
  access_journal: 'Доступ к уроку',
  user: 'Профиль',
  user_profile: 'Данные профиля',
  profile_field_value: 'Дополнительное поле профиля',
}

const fields: Record<string, string> = {
  name: 'Имя / название',
  title: 'Название',
  email: 'Email',
  phone: 'Телефон',
  login: 'Логин',
  custom_id: 'Внешний ID',
  avatar_path: 'Фото',
  roles: 'Роли',
  departments: 'Отделы (ID)',
  franchises: 'Филиалы (ID)',
  parents: 'Родители (ID)',
  children: 'Дети (ID)',
  status: 'Статус',
  is_disabled: 'Отключён',
  deleted_at: 'Дата удаления',
  about: 'О себе',
  birth_date: 'Дата рождения',
  sex: 'Пол',
  city: 'Город',
  address: 'Адрес',
  education: 'Образование',
  experience: 'Опыт',
  notes: 'Заметки',
  parent_name: 'Имя родителя',
  parent_phone: 'Телефон родителя',
  parent_email: 'Email родителя',
  parent_telegram: 'Telegram родителя',
  parent_whatsapp: 'WhatsApp родителя',
  timezone: 'Часовой пояс',
  learning_class: 'Класс обучения',
  age: 'Возраст',
  about_student: 'Об ученике',
  about_parent: 'О родителе',
  url_conference: 'Ссылка педагога',
  url_conference_for_children: 'Ссылка ученика',
  link_to_vk: 'ВКонтакте',
  telebos_room_id: 'Комната ВКС',
  date_conference: 'Дата занятия',
  start_time: 'Начало',
  end_time: 'Окончание',
  duration: 'Длительность',
  speaker_id: 'Педагог (ID)',
  attendees: 'Участники',
  groups: 'Группы',
  comment: 'Комментарий',
  comment_to_dz: 'Комментарий к ДЗ',
  lesson_topic: 'Тема занятия',
  link_to_meeting: 'Ссылка педагога',
  link_to_meeting_for_children: 'Ссылка ученика',
  grade: 'Оценка',
  label: 'Подпись оценки',
  weight: 'Вес оценки',
  type: 'Тип',
  color: 'Цвет',
  student_lesson_status: 'Статус ученика на занятии',
  value: 'Значение',
  list: 'Список',
  member_id: 'Ученик (ID)',
  user_id: 'Пользователь (ID)',
  event_report_id: 'Занятие (ID)',
  journal_id: 'Строка журнала (ID)',
  column_id: 'Колонка (ID)',
  field_id: 'Поле (ID)',
  group_id: 'Группа (ID)',
  direction_id: 'Курс (ID)',
  event_id: 'Серия занятий (ID)',
  date: 'Дата',
  end_date: 'Дата окончания',
  period: 'Период',
  visibility: 'Видимость',
  order: 'Порядок',
  audience_id: 'Аудитория (ID)',
  audience_number: 'Аудитория',
  is_rescheduled: 'Перенесено',
  pinned: 'Закреплено',
  isOpen: 'Открытое занятие',
  salary: 'Зарплата',
  is_paid: 'Оплачено',
  topic_name: 'Тема КТП',
  topic_name_override: 'Название темы',
  topic_id: 'Тема (ID)',
  lesson_id: 'Урок (ID)',
  theme_id: 'Тема курса (ID)',
  block_id: 'Блок (ID)',
  plan_date: 'Плановая дата',
  report_id: 'Занятие (ID)',
  reports: 'Привязанные занятия (ID)',
  homework: 'Домашнее задание',
  homework_rich: 'Форматированное ДЗ',
  homework_files: 'Файлы ДЗ (ID)',
  homework_date: 'Дата выдачи ДЗ',
  homework_report_id: 'Занятие выдачи ДЗ (ID)',
  homework_id: 'Домашнее задание (ID)',
  sort_order: 'Порядок строки КТП',
}

export const historyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  const document = normalizeTiptapValue(value)
  if (document?.type === 'doc') return getMessageText(document) || '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const columns: ColumnDef<ChangeHistoryRow>[] = [
  {
    header: 'Дата и время',
    id: 'date',
    size: 160,
    cell: ({ row }) => (
      <TextCell
        text={dayjs.unix(row.original.created_at).format('DD.MM.YYYY HH:mm:ss')}
      />
    ),
  },
  {
    header: 'Кто изменил',
    id: 'actor',
    size: 180,
    cell: ({ row }) => <TextCell text={row.original.actor_name || 'Система'} />,
  },
  {
    header: 'Что изменено',
    id: 'entity',
    size: 230,
    cell: ({ row }) => (
      <div className={cn.cell}>
        <span>
          {entities[row.original.entity_type] || row.original.entity_type} №
          {row.original.entity_id}
        </span>
        <span>{row.original.entity_label}</span>
        {row.original.subject_id && (
          <span>Пользователь №{row.original.subject_id}</span>
        )}
      </div>
    ),
  },
  {
    header: 'Действие',
    id: 'action',
    size: 120,
    cell: ({ row }) => (
      <TextCell
        text={row.original.action === 'delete' ? 'Удаление' : 'Изменение'}
      />
    ),
  },
  {
    header: 'Изменения: было → стало',
    id: 'changes',
    size: 480,
    cell: ({ row }) => (
      <div className={cn.cell}>
        {Object.entries(row.original.changes).map(([field, change]) => (
          <div key={field}>
            <strong>{fields[field] || field}:</strong>{' '}
            {historyValue(change.old)} → {historyValue(change.new)}
          </div>
        ))}
      </div>
    ),
  },
  {
    header: 'Источник',
    id: 'source',
    size: 200,
    cell: ({ row }) => <TextCell text={row.original.source} />,
  },
]

const HistoryTable = ({
  rows,
  loading = false,
}: {
  rows: ChangeHistoryRow[]
  loading?: boolean
}) => (
  <CustomTable<ChangeHistoryRow>
    columns={columns}
    data={rows}
    loading={loading}
  />
)

export default HistoryTable
